import { useEffect, useMemo, useState } from "react";

import { useAtom } from "jotai";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

import { settingsDialogAtom } from "src/components/SettingsDialog/SettingsDialog";
import { SettingsSection } from "src/components/SettingsDialog/SettingsSection";
import { UserAvatar } from "src/components/ui/avatar";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "src/components/ui/dialog";
import { useAuth } from "src/lib/auth";
import { useAdminPrivacyMode } from "src/lib/hooks/useAdminPrivacyMode";
import { useApiMutation } from "src/lib/hooks/useApi";
import { TAKEOVER_BILLING_MUTATION } from "src/lib/queries/billing";

export function BillingSection({ store }) {
  const { currentUser } = useAuth();
  const { privacyMode } = useAdminPrivacyMode();
  const [, setSettings] = useAtom(settingsDialogAtom);
  const [takeoverDialogOpen, setTakeoverDialogOpen] = useState(false);
  const [revealBillingUser, setRevealBillingUser] = useState(!privacyMode);

  const { mutationApi: takeoverBilling, loading: loadingTakeoverBilling } =
    useApiMutation({
      mutation: TAKEOVER_BILLING_MUTATION,
      onSuccess: () => {
        toast.success("Successfully took over billing.");
      },
    });

  const billingUser = useMemo(() => {
    return store?.users?.find((user) => user.isBillingUser);
  }, [store]);
  const isBillingUser = billingUser?.user?.id === currentUser?.id;
  const storeLabel = privacyMode
    ? process.env.APP_NAME
    : store?.description || "This store";

  useEffect(() => {
    setRevealBillingUser(!privacyMode);
  }, [privacyMode]);

  const billingRevealed = !privacyMode || revealBillingUser;
  const billingUserName = billingRevealed
    ? billingUser?.user?.name || "Unknown User"
    : "Hidden user";
  const billingUserEmail = billingRevealed
    ? billingUser?.user?.email || "Unknown Email"
    : "*****";

  return (
    <SettingsSection
      title="Billing User"
      description={`${storeLabel} is billed to ${isBillingUser ? "you" : "the following user"}`}
    >
      <div className="flex justify-between gap-4">
        <div className="flex items-center gap-4">
          <UserAvatar user={billingRevealed ? billingUser?.user : null} />
          <div className="flex flex-col">
            <div className="text-sm">{billingUserName}</div>
            <div className="text-muted-foreground text-xs">
              {billingUserEmail}
            </div>
          </div>
          {privacyMode && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={() => setRevealBillingUser((prev) => !prev)}
              aria-label={
                billingRevealed ? "Hide billing user" : "Show billing user"
              }
            >
              {billingRevealed ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </Button>
          )}
        </div>

        <Button
          variant="secondary"
          className="w-fit"
          onClick={() => {
            if (isBillingUser) {
              setSettings((prev) => ({
                ...prev,
                activeTab: "billing",
                manageParam: null,
                sectionTarget: null,
                sectionTargetNonce: 0,
              }));
            } else {
              setTakeoverDialogOpen(true);
            }
          }}
        >
          {isBillingUser ? "Manage Billing" : "Takeover Billing"}
        </Button>
      </div>

      <Dialog open={takeoverDialogOpen} onOpenChange={setTakeoverDialogOpen}>
        <DialogContent>
          <DialogTitle>Store Billing Takeover</DialogTitle>
          <DialogDescription>
            Are you sure you want to take over billing for this store?
            <br />
            You will become the new billing user and will be responsible for all
            future payments.
          </DialogDescription>

          <DialogFooter>
            <Button
              variant="secondary"
              disabled={loadingTakeoverBilling}
              onClick={() => setTakeoverDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={loadingTakeoverBilling}
              onClick={async () => {
                await takeoverBilling({
                  id: store.id,
                });

                setTakeoverDialogOpen(false);
              }}
            >
              Confirm Takeover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsSection>
  );
}
